
import { useAuth } from "@/lib/AuthContext";
import { useSubscription } from "@/lib/SubscriptionContext";
import { Navigate, useLocation } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { getChitPools } from "@/lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface Props {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<Props> = ({ children }) => {
  const { user, loading } = useAuth();
  const { planLimits, isLoading: subscriptionLoading, userPlan } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [isCheckingLimits, setIsCheckingLimits] = useState(false);
  const [limitType, setLimitType] = useState<'chitFunds' | 'members' | null>(null);

  useEffect(() => {
    // Only check limits for chit-pools creation pages
    if (user && 
        !subscriptionLoading && 
        (location.pathname.includes('/chit-pools/new') || location.pathname === '/chit-pools/new')) {
      
      const checkChitPoolLimits = async () => {
        setIsCheckingLimits(true);
        try {
          const pools = await getChitPools();
          
          // Only block creation pages when at limit
          if (pools.length >= planLimits.maxChitFunds) {
            setIsOverLimit(true);
            setLimitType('chitFunds');
            toast.error(`Plan limit exceeded. Your ${userPlan?.name || "Free"} plan allows up to ${planLimits.maxChitFunds} chit ${planLimits.maxChitFunds === 1 ? 'fund' : 'funds'}.`);
          } else {
            setIsOverLimit(false);
          }
        } catch (error) {
          console.error("Error checking limits:", error);
          toast.error("Error checking subscription limits");
        } finally {
          setIsCheckingLimits(false);
        }
      };
      
      checkChitPoolLimits();
    } else {
      // Reset for non-creation pages
      setIsOverLimit(false);
    }
  }, [user, subscriptionLoading, location.pathname, planLimits]);

  if (loading || subscriptionLoading || isCheckingLimits) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isOverLimit) {
    const limitMessage = limitType === 'chitFunds' 
      ? `Your current ${userPlan?.name || "Free"} plan allows managing up to ${planLimits.maxChitFunds} chit ${planLimits.maxChitFunds === 1 ? "fund" : "funds"}.`
      : `Your current ${userPlan?.name || "Free"} plan allows up to ${planLimits.maxMembers} members per chit fund.`;

    return (
      <div className="p-8">
        <Alert className="bg-amber-50 border-amber-200 mb-6">
          <AlertTitle className="text-amber-800 text-lg font-medium">Subscription Limit Reached</AlertTitle>
          <AlertDescription className="text-amber-700">
            <p className="mb-4">{limitMessage} Please upgrade your plan to access more features.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate("/#pricing")} variant="default">Upgrade Plan</Button>
              <Button onClick={() => navigate("/chit-pools")} variant="outline">View Existing Chit Pools</Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
